/**
 * Estima — shared loan math (JS mirror of backend `loan_math.py`).
 * Must stay numerically identical to the Python version. Any divergence >
 * ₹1 fails the backend test fixture.
 */

export class LoanMathError extends Error {
  constructor(msg) { super(msg); this.name = "LoanMathError"; }
}

const r_ = (annualRatePct) => Number(annualRatePct) / 12 / 100;

/** Standard EMI formula. Zero-rate safe → P/n. */
export function emi(P, annualRatePct, n) {
  P = Number(P); n = Math.floor(Number(n));
  if (P <= 0 || n <= 0) return 0;
  const r = r_(annualRatePct);
  if (r === 0) return P / n;
  return (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

/** n = ceil(-ln(1 - r·P/EMI) / ln(1+r)). Throws if EMI ≤ monthly interest. */
export function remainingTenure(P, annualRatePct, emiAmount) {
  P = Number(P); const E = Number(emiAmount);
  if (P <= 0 || E <= 0) throw new LoanMathError("Principal and EMI must both be positive");
  const r = r_(annualRatePct);
  if (r === 0) return Math.ceil(P / E);
  const monthlyInterest = P * r;
  if (E <= monthlyInterest + 1e-9) {
    throw new LoanMathError(
      "Your EMI does not cover the monthly interest. Please check EMI or rate."
    );
  }
  const ratio = 1 - (r * P) / E;
  return Math.ceil(-Math.log(ratio) / Math.log(1 + r));
}

function addMonths(d, months) {
  const y = d.getFullYear();
  const m = d.getMonth(); // 0-indexed
  const total = y * 12 + m + months;
  const y2 = Math.floor(total / 12);
  const m2 = total % 12;
  const lastDay = new Date(y2, m2 + 1, 0).getDate();
  return new Date(y2, m2, Math.min(d.getDate(), lastDay));
}

/**
 * Full amortisation schedule.
 * @param {number} P
 * @param {number} annualRatePct
 * @param {number} emiAmount
 * @param {Array<{month:number, amount:number}>} prepayments
 * @param {Date|null} startDate
 * @param {number} maxMonths
 * @returns {{rows: Array, total_interest: number, months: number, closure_date: string|null, total_prepayment: number}}
 */
export function schedule(P, annualRatePct, emiAmount, prepayments = [], startDate = null, maxMonths = 1200) {
  P = Number(P); const E = Number(emiAmount);
  if (P <= 0) return { rows: [], total_interest: 0, months: 0, closure_date: null, total_prepayment: 0 };
  const r = r_(annualRatePct);
  const preBy = {};
  for (const p of prepayments || []) {
    const m = Math.floor(Number(p.month));
    preBy[m] = (preBy[m] || 0) + Number(p.amount);
  }
  const rows = [];
  let opening = P;
  let totalInterest = 0;
  let totalPrepayment = 0;
  let month = 0;
  while (opening > 1e-6 && month < maxMonths) {
    month += 1;
    const interest = opening * r;
    let emiPaid = E;
    let principalComp = emiPaid - interest;
    if (principalComp >= opening) {
      principalComp = opening;
      emiPaid = principalComp + interest;
    }
    let closing = opening - principalComp;
    let prepay = preBy[month] || 0;
    if (prepay > closing) prepay = closing;
    closing -= prepay;
    totalInterest += interest;
    totalPrepayment += prepay;
    const rowDate = startDate ? addMonths(startDate, month - 1) : null;
    rows.push({
      month_no: month,
      date: rowDate ? rowDate.toISOString().slice(0, 10) : null,
      opening_balance: opening,
      interest,
      principal: principalComp,
      prepayment: prepay,
      closing_balance: Math.max(closing, 0),
      emi_paid: emiPaid,
    });
    opening = closing;
    if (opening <= 1e-6) break;
  }
  const closure = startDate && month > 0 ? addMonths(startDate, month - 1) : null;
  return {
    rows,
    total_interest: totalInterest,
    months: month,
    closure_date: closure ? closure.toISOString().slice(0, 10) : null,
    total_prepayment: totalPrepayment,
  };
}

/** Expand recurring prepayment lines into a flat `{month, amount}[]`. */
export function expandPrepayments(lines, maxMonths = 480) {
  const out = [];
  for (const line of lines || []) {
    if (!line || !line.amount || Number(line.amount) <= 0) continue;
    const amt = Number(line.amount);
    const start = Math.max(1, Math.floor(Number(line.month) || 1));
    const freq = line.frequency || "one_time";
    const times = Math.max(1, Math.floor(Number(line.times) || 1));
    if (freq === "one_time") {
      out.push({ month: start, amount: amt });
    } else if (freq === "yearly") {
      for (let k = 0; k < times && start + 12 * k <= maxMonths; k++)
        out.push({ month: start + 12 * k, amount: amt });
    } else if (freq === "monthly") {
      for (let k = 0; k < times && start + k <= maxMonths; k++)
        out.push({ month: start + k, amount: amt });
    }
  }
  return out;
}

/** Schedule with annual EMI step-up — EMI bumps by `stepUpPct%` every 12 months. */
export function scheduleStepUp(P, annualRatePct, baseEmi, stepUpPct, prepayments = [], maxMonths = 1200) {
  P = Number(P); let E = Number(baseEmi);
  const r = Number(annualRatePct) / 12 / 100;
  const preBy = {};
  for (const p of prepayments || []) {
    const m = Math.floor(Number(p.month));
    preBy[m] = (preBy[m] || 0) + Number(p.amount);
  }
  const rows = [];
  let opening = P;
  let totalInterest = 0;
  let totalPrepay = 0;
  let month = 0;
  const bump = Math.max(0, Math.min(20, Number(stepUpPct) || 0)) / 100;
  while (opening > 1e-6 && month < maxMonths) {
    month += 1;
    if (bump > 0 && month > 1 && month % 12 === 1) E = E * (1 + bump);
    const interest = opening * r;
    let emiPaid = E;
    let principalComp = emiPaid - interest;
    if (principalComp >= opening) {
      principalComp = opening;
      emiPaid = principalComp + interest;
    }
    let closing = opening - principalComp;
    let prepay = preBy[month] || 0;
    if (prepay > closing) prepay = closing;
    closing -= prepay;
    totalInterest += interest;
    totalPrepay += prepay;
    rows.push({
      month_no: month,
      opening_balance: opening,
      interest, principal: principalComp,
      prepayment: prepay,
      closing_balance: Math.max(closing, 0),
      emi_paid: emiPaid,
    });
    opening = closing;
    if (opening <= 1e-6) break;
  }
  return { rows, total_interest: totalInterest, months: month, total_prepayment: totalPrepay };
}

/** "X yrs Y mths" formatter. */
export function formatTenure(months) {
  const m = Math.round(Number(months));
  if (m <= 0) return "0 mths";
  const yrs = Math.floor(m / 12);
  const mths = m % 12;
  if (yrs === 0) return `${mths} mths`;
  if (mths === 0) return `${yrs} yrs`;
  return `${yrs} yrs ${mths} mths`;
}

/** Indian digit-grouping formatter: ₹12,34,567 / ₹12.34L / ₹1.23Cr */
export function formatINR(amount, short = false) {
  const n = Number(amount);
  if (!isFinite(n)) return "₹0";
  const neg = n < 0;
  const a = Math.abs(n);
  if (short) {
    if (a >= 1e7) return `${neg ? "-" : ""}₹${(a / 1e7).toFixed(2)}Cr`;
    if (a >= 1e5) return `${neg ? "-" : ""}₹${(a / 1e5).toFixed(2)}L`;
  }
  const whole = Math.round(a).toString();
  let grouped;
  if (whole.length <= 3) {
    grouped = whole;
  } else {
    const last3 = whole.slice(-3);
    let rest = whole.slice(0, -3);
    const parts = [];
    while (rest.length > 2) {
      parts.unshift(rest.slice(-2));
      rest = rest.slice(0, -2);
    }
    if (rest) parts.unshift(rest);
    grouped = parts.join(",") + "," + last3;
  }
  return `${neg ? "-" : ""}₹${grouped}`;
}
